import React, { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  ArrowUpIcon,
  ArrowDownIcon,
  PhotoIcon
} from '@heroicons/react/24/outline';
import { Table, Button, Modal, Form, Input, Switch, Popconfirm, message, Upload, Progress } from 'antd';
import { UploadOutlined, PlusOutlined } from '@ant-design/icons';
import { TeamActivity } from '../../types';
import apiService from '../../services/api';
import TeamAlbum from '../../components/TeamAlbum';

const { TextArea } = Input;

const TeamActivityManagement: React.FC = () => {
  const [activities, setActivities] = useState<TeamActivity[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingActivity, setEditingActivity] = useState<TeamActivity | null>(null);
  const [form] = Form.useForm();
  const [uploading, setUploading] = useState(false);
  const [batchUploadVisible, setBatchUploadVisible] = useState(false);
  const [batchForm] = Form.useForm();
  const [showAlbumSelector, setShowAlbumSelector] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<any[]>([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState<'uploading' | 'success' | 'error' | 'idle'>('idle');

  useEffect(() => {
    fetchActivities();
  }, []);

  const fetchActivities = async () => {
    setLoading(true);
    try {
      const response = await apiService.get<TeamActivity[]>('/team-activities/admin');
      if (response.success && response.data) {
        setActivities(response.data.sort((a, b) => a.order - b.order));
      }
    } catch (error) {
      console.error('获取团队活动失败:', error);
      message.error('获取团队活动失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingActivity(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (activity: TeamActivity) => {
    setEditingActivity(activity);
    form.setFieldsValue({
      title: activity.title,
      description: activity.description,
      imageUrl: activity.imageUrl,
      link: activity.link,
      order: activity.order,
      isActive: activity.isActive
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await apiService.delete(`/team-activities/${id}`);
      message.success('删除成功');
      fetchActivities();
    } catch (error) {
      console.error('删除失败:', error);
      message.error('删除失败');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingActivity) {
        await apiService.put(`/team-activities/${editingActivity._id}`, values);
        message.success('更新成功');
      } else {
        await apiService.post('/team-activities', values);
        message.success('创建成功');
      }
      setModalVisible(false);
      fetchActivities();
    } catch (error) {
      console.error('保存失败:', error);
      message.error('保存失败');
    }
  };

  const handleOrderChange = async (activity: TeamActivity, direction: 'up' | 'down') => {
    const currentIndex = activities.findIndex(a => a._id === activity._id);
    if (currentIndex === -1) return;

    const newOrder = direction === 'up' 
      ? Math.max(0, activity.order - 1)
      : activity.order + 1;

    try {
      await apiService.put(`/team-activities/${activity._id}`, {
        ...activity,
        order: newOrder
      });
      message.success('排序更新成功');
      fetchActivities();
    } catch (error) {
      console.error('排序更新失败:', error);
      message.error('排序更新失败');
    }
  };

  // 单张图片上传
  const handleImageUpload = async (file: File) => {
    setUploading(true);
    setUploadStatus('uploading');
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      formData.append('image', file);
      
      const response = await apiService.upload<{url: string}>('/upload/image', formData, {
        onUploadProgress: (progressEvent: any) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          }
        }
      });
      
      if (response.success && response.data) {
        form.setFieldsValue({ imageUrl: response.data.url });
        setUploadStatus('success');
        message.success('图片上传成功');
      }
    } catch (error) {
      console.error('图片上传失败:', error);
      setUploadStatus('error');
      message.error('图片上传失败');
    } finally {
      setUploading(false);
      // 3秒后重置状态
      setTimeout(() => {
        setUploadStatus('idle');
        setUploadProgress(0);
      }, 3000);
    }
  };

  // 从相册选择图片
  const handleImageSelectFromAlbum = (imageUrl: string) => {
    form.setFieldsValue({ imageUrl });
    setShowAlbumSelector(false);
    message.success('已选择图片');
  };

  // 批量上传
  const handleBatchUpload = async (values: any) => {
    const { images, baseTitle, baseDescription, baseLink } = values;
    
    if (!images || images.length === 0) {
      message.error('请选择要上传的图片');
      return;
    }

    setUploading(true);
    setUploadStatus('uploading');
    setUploadProgress(0);
    
    try {
      // 使用批量上传API
      const formData = new FormData();
      images.forEach((file: File) => {
        formData.append('images', file);
      });
      
      const response = await apiService.upload<{url: string}[]>('/upload/images', formData, {
        onUploadProgress: (progressEvent: any) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          }
        }
      });
      
      if (response.success && response.data) {
        const uploadedUrls = response.data.map(item => item.url);

        // 批量创建团队活动（逐个发送请求，避免占用过大）
        for (let index = 0; index < uploadedUrls.length; index++) {
          const imageUrl = uploadedUrls[index];
          const activityPayload = {
            title: `${baseTitle} ${index + 1}`,
            description: baseDescription,
            imageUrl,
            link: baseLink,
            order: activities.length + index,
            isActive: true
          };
          await apiService.post('/team-activities', activityPayload);
        }

        setUploadStatus('success');
        message.success(`成功创建 ${uploadedUrls.length} 个团队活动`);
        setBatchUploadVisible(false);
        batchForm.resetFields();
        setSelectedFiles([]);
        fetchActivities();
      } else {
        throw new Error('批量上传失败');
      }
    } catch (error) {
      console.error('批量上传失败:', error);
      setUploadStatus('error');
      message.error('批量上传失败: ' + (error as Error).message);
    } finally {
      setUploading(false);
      // 3秒后重置状态
      setTimeout(() => {
        setUploadStatus('idle');
        setUploadProgress(0);
      }, 3000);
    }
  };

  // 上传配置
  const uploadProps = {
    name: 'image',
    showUploadList: false,
    beforeUpload: (file: File) => {
      handleImageUpload(file);
      return false; // 阻止默认上传行为
    },
    accept: 'image/*'
  };

  const columns = [
    {
      title: '排序',
      key: 'order',
      width: 100,
      render: (activity: TeamActivity) => (
        <div className="flex items-center space-x-1">
          <button
            onClick={() => handleOrderChange(activity, 'up')}
            disabled={activity.order === 0}
            className="p-1 text-gray-500 hover:text-blue-600 disabled:opacity-30"
          >
            <ArrowUpIcon className="h-4 w-4" />
          </button>
          <span className="text-sm font-medium">{activity.order}</span>
          <button
            onClick={() => handleOrderChange(activity, 'down')}
            disabled={activity.order === activities.length - 1}
            className="p-1 text-gray-500 hover:text-blue-600 disabled:opacity-30"
          >
            <ArrowDownIcon className="h-4 w-4" />
          </button>
        </div>
      )
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      render: (text: string) => (
        <span className="font-medium">{text}</span>
      )
    },
    {
      title: '图片',
      key: 'image',
      width: 120,
      render: (activity: TeamActivity) => (
        <div className="w-16 h-12 rounded overflow-hidden">
          <img
            src={activity.imageUrl}
            alt={activity.title}
            className="w-full h-full object-cover"
          />
        </div>
      )
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => (
        <span className="text-gray-600 text-sm line-clamp-2">
          {text || '暂无描述'}
        </span>
      )
    },
    {
      title: '链接',
      dataIndex: 'link',
      key: 'link',
      render: (text: string) => (
        <span className="text-blue-600 text-sm">
          {text ? '已设置' : '未设置'}
        </span>
      )
    },
    {
      title: '创建者',
      key: 'createdBy',
      width: 120,
      render: (activity: TeamActivity) => (
        <div className="text-sm">
          <div className="font-medium">
            {activity.createdBy?.name || '未知用户'}
          </div>
          <div className="text-xs text-gray-500">
            {activity.isAdminCreated ? '管理员' : '普通用户'}
          </div>
        </div>
      )
    },
    {
      title: '状态',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive: boolean, activity: TeamActivity) => (
        <span className={`px-2 py-1 rounded-full text-xs ${
          isActive 
            ? 'bg-green-100 text-green-800' 
            : 'bg-gray-100 text-gray-800'
        }`}>
          {isActive ? '启用' : '禁用'}
        </span>
      )
    },
    {
      title: '操作',
      key: 'actions',
      width: 150,
      render: (activity: TeamActivity) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleEdit(activity)}
            className="p-1 text-blue-600 hover:text-blue-800"
          >
            <PencilIcon className="h-4 w-4" />
          </button>
          <Popconfirm
            title="确定要删除这个活动吗？"
            onConfirm={() => handleDelete(activity._id)}
            okText="确定"
            cancelText="取消"
          >
            <button className="p-1 text-red-600 hover:text-red-800">
              <TrashIcon className="h-4 w-4" />
            </button>
          </Popconfirm>
        </div>
      )
    }
  ];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">团队活动管理</h1>
        <div className="flex space-x-3">
          <Button
            type="primary"
            icon={<PhotoIcon className="h-4 w-4" />}
            onClick={() => setBatchUploadVisible(true)}
          >
            批量上传
          </Button>
          <Button
            type="primary"
            icon={<PlusIcon className="h-4 w-4" />}
            onClick={handleCreate}
          >
            添加活动
          </Button>
        </div>
      </div>

      <Table
        dataSource={activities}
        columns={columns}
        loading={loading}
        rowKey="_id"
        pagination={false}
        className="bg-white rounded-lg shadow"
      />

      <Modal
        title={editingActivity ? '编辑团队活动' : '添加团队活动'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            isActive: true,
            order: 0
          }}
        >
          <Form.Item
            name="title"
            label="活动标题"
            rules={[{ required: true, message: '请输入活动标题' }]}
          >
            <Input placeholder="请输入活动标题" />
          </Form.Item>

          <Form.Item
            name="description"
            label="活动描述"
          >
            <TextArea 
              rows={3} 
              placeholder="请输入活动描述（可选）"
            />
          </Form.Item>

          <Form.Item
            name="imageUrl"
            label="图片链接"
            rules={[{ required: false }]}
          >
            <Input placeholder="请输入图片链接（可选，或使用下方上传功能）" />
          </Form.Item>
          
          <Form.Item label="图片管理">
            <div className="space-y-3">
              <div className="flex space-x-2">
                <Upload {...uploadProps}>
                  <Button 
                    icon={<UploadOutlined />} 
                    loading={uploading}
                    disabled={uploading}
                  >
                    本地上传
                  </Button>
                </Upload>
                <Button 
                  icon={<PhotoIcon className="h-4 w-4" />}
                  onClick={() => setShowAlbumSelector(true)}
                >
                  从相册选择
                </Button>
              </div>
              {uploadStatus === 'uploading' && (
                <div className="space-y-2">
                  <Progress 
                    percent={uploadProgress} 
                    status="active"
                    strokeColor="#1890ff"
                  />
                  <div className="text-xs text-blue-600 text-center">
                    上传中... {uploadProgress}%
                  </div>
                </div>
              )}
              {uploadStatus === 'success' && (
                <div className="text-xs text-green-600 text-center">
                  ✅ 上传成功！
                </div>
              )}
              {uploadStatus === 'error' && (
                <div className="text-xs text-red-600 text-center">
                  ❌ 上传失败，请重试
                </div>
              )}
              <div className="text-xs text-gray-500">
                支持 JPG、PNG、GIF 格式，最大 10MB
              </div>
              <div className="text-xs text-blue-600">
                💡 建议尺寸：1920×1080 或 16:9 比例，以获得最佳显示效果
              </div>
            </div>
          </Form.Item>

          <Form.Item
            name="link"
            label="相关链接"
          >
            <Input placeholder="请输入相关链接（可选）" />
          </Form.Item>

          <Form.Item
            name="order"
            label="排序"
            rules={[{ required: true, message: '请输入排序' }]}
          >
            <Input type="number" placeholder="数字越小越靠前" />
          </Form.Item>

          <Form.Item
            name="isActive"
            label="启用状态"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item className="mb-0">
            <div className="flex justify-end space-x-3">
              <Button onClick={() => setModalVisible(false)}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                {editingActivity ? '更新' : '创建'}
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      {/* 批量上传Modal */}
      <Modal
        title="批量上传团队活动"
        open={batchUploadVisible}
        onCancel={() => setBatchUploadVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={batchForm}
          layout="vertical"
          onFinish={handleBatchUpload}
          initialValues={{
            baseTitle: '团队活动',
            baseDescription: '',
            baseLink: ''
          }}
          onFinishFailed={(errorInfo) => {
            console.log('表单验证失败:', errorInfo);
          }}
        >
          <Form.Item
            name="baseTitle"
            label="活动标题前缀"
            rules={[{ required: true, message: '请输入活动标题前缀' }]}
          >
            <Input placeholder="如：课题组会议" />
          </Form.Item>

          <Form.Item
            name="baseDescription"
            label="活动描述"
          >
            <TextArea 
              rows={3} 
              placeholder="所有活动的通用描述（可选）"
            />
          </Form.Item>

          <Form.Item
            name="baseLink"
            label="相关链接"
          >
            <Input placeholder="相关链接（可选）" />
          </Form.Item>

          <Form.Item 
            name="images"
            label="选择图片"
            rules={[
              {
                validator: (_, value) => {
                  if (!value || value.length === 0) {
                    return Promise.reject(new Error('请至少选择一张图片'));
                  }
                  return Promise.resolve();
                }
              }
            ]}
            help="请至少选择一张图片"
          >
            <div>
              <Upload
                listType="picture-card"
                fileList={selectedFiles}
                beforeUpload={() => false}
                multiple
                accept="image/*"
                onChange={({ fileList }) => {
                  // 只保留有效的文件
                  const validFiles = fileList.filter(file => file.originFileObj);
                  setSelectedFiles(validFiles);
                  
                  const images = validFiles.map(f => f.originFileObj).filter(Boolean);
                  batchForm.setFieldsValue({ images });
                  
                  // 触发表单验证
                  batchForm.validateFields(['images']);
                }}
              >
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>选择图片</div>
                </div>
              </Upload>
              
              {uploadStatus === 'uploading' && (
                <div className="space-y-2 mt-3">
                  <Progress 
                    percent={uploadProgress} 
                    status="active"
                    strokeColor="#1890ff"
                  />
                  <div className="text-xs text-blue-600 text-center">
                    批量上传中... {uploadProgress}%
                  </div>
                </div>
              )}
              {uploadStatus === 'success' && (
                <div className="text-xs text-green-600 text-center mt-2">
                  ✅ 批量上传成功！
                </div>
              )}
              {uploadStatus === 'error' && (
                <div className="text-xs text-red-600 text-center mt-2">
                  ❌ 批量上传失败，请重试
                </div>
              )}
              
              <div className="text-xs text-gray-500 mt-2">
                支持多选，最多10张图片，每张最大10MB
              </div>
              <div className="text-xs text-blue-600 mt-1">
                💡 建议尺寸：1920×1080 或 16:9 比例，以获得最佳显示效果
              </div>
            </div>
          </Form.Item>

          <Form.Item className="mb-0">
            <div className="flex justify-end space-x-3">
              <Button onClick={() => setBatchUploadVisible(false)}>
                取消
              </Button>
              <Button 
                type="primary" 
                htmlType="submit"
                loading={uploading}
                disabled={uploading}
              >
                {uploading ? '上传中...' : '开始上传'}
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      {/* 相册选择器Modal */}
      <Modal
        title="从团队活动相册选择图片"
        open={showAlbumSelector}
        onCancel={() => setShowAlbumSelector(false)}
        footer={null}
        width={800}
        styles={{ body: { maxHeight: '70vh', overflow: 'auto' } }}
      >
        <TeamAlbum 
          onImageSelect={handleImageSelectFromAlbum}
          isSelectionMode={true}
        />
      </Modal>
    </div>
  );
};

export default TeamActivityManagement;
